import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
export const documentsKnowledgeSignedPolicyArtifactsCapability = 'documents.knowledge-signed-policy-artifacts.v1';
export const documentsKnowledgeSignedPolicyFactsCapability = 'knowledge.documents-signed-policy-facts.v1';
export const documentsKnowledgeSignedPolicyAcknowledgementCapability = 'knowledge.documents-signed-policy-acknowledgement.v1';
export const documentsKnowledgeSignedPolicyFactsSlot = 'documents.knowledge-signed-policy-facts';
export const documentsKnowledgeSignedPolicyAcknowledgementSlot = 'documents.knowledge-signed-policy-acknowledgements';
export const documentsKnowledgeSignedPolicyFactsProviderId = 'knowledge.server.documents-signed-policy-facts';
export const documentsKnowledgeSignedPolicyAcknowledgementProviderId = 'knowledge.server.documents-signed-policy-acknowledgement';
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const reasonSchema = z.string().trim().min(5).max(500);
const signingConfigurationSchema = z.strictObject({
    enabled: z.literal(true),
    pageCount: z.number().int().min(1).max(250),
    initialsRequired: z.literal(true),
    typedNameRequired: z.literal(true),
    drawnSignatureRequired: z.literal(true),
    consentText: z.string().trim().min(1).max(4_000),
});
export const documentsKnowledgeSignedPolicyFactsRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(documentsKnowledgeSignedPolicyArtifactsCapability),
    resourceType: z.literal('documents.knowledge-signed-policy-artifact'),
    resourceId: z.string().uuid(),
    documentId: z.string().uuid(),
    expectedVersionId: z.string().uuid(),
});
export const documentsKnowledgeSignedPolicyFactsResponseSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(documentsKnowledgeSignedPolicyFactsCapability),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    documentId: z.string().uuid(),
    versionId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    title: z.string().trim().min(1).max(200),
    documentType: z.enum(['sop', 'policy', 'safety', 'reference', 'faq', 'guide']),
    bodyMarkdown: z.string().trim().min(1).max(500_000),
    sourceContentHash: sha256Schema,
    publishedAt: z.string().datetime({ offset: true }),
    signing: signingConfigurationSchema,
    factHash: sha256Schema,
});
export const documentsKnowledgeSignedPolicyAcknowledgementRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(documentsKnowledgeSignedPolicyAcknowledgementCapability),
    sourceCapability: z.literal(documentsKnowledgeSignedPolicyArtifactsCapability),
    commandId: z.string().uuid(),
    documentId: z.string().uuid(),
    versionId: z.string().uuid(),
    sourceContentHash: sha256Schema,
    signedProofHash: sha256Schema,
    acknowledgementText: z.string().trim().min(1).max(500),
});
export const documentsKnowledgeSignedPolicyAcknowledgementResponseSchema = z.strictObject({
    state: z.literal('accepted'),
    capability: z.literal(documentsKnowledgeSignedPolicyAcknowledgementCapability),
    tenantId: z.string().uuid(),
    documentId: z.string().uuid(),
    versionId: z.string().uuid(),
    membershipId: z.string().uuid(),
    acknowledgementId: z.string().uuid(),
    acknowledgedAt: z.string().datetime({ offset: true }),
    signedProofHash: sha256Schema.nullable(),
    proofBound: z.boolean(),
    idempotent: z.boolean(),
});
export const documentsKnowledgeSignedPolicyPageInitialSchema = z.strictObject({
    pageNumber: z.number().int().min(1).max(250),
    initials: z.string().trim().min(1).max(20),
});
export const documentsCreateKnowledgeSignedPolicyArtifactSchema = z.strictObject({
    commandId: z.string().uuid(),
    documentId: z.string().uuid(),
    expectedVersionId: z.string().uuid(),
    acknowledgementText: z.string().trim().min(1).max(500),
    signerInitials: z.string().trim().min(1).max(20),
    pageInitials: z.array(documentsKnowledgeSignedPolicyPageInitialSchema).max(250),
    typedName: z.string().trim().min(1).max(240),
    signatureSha256: sha256Schema,
    signatureArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable().default(null),
    consentAccepted: z.literal(true),
    reason: reasonSchema,
}).superRefine((value, context) => {
    const pageNumbers = value.pageInitials.map(({ pageNumber }) => pageNumber);
    if (new Set(pageNumbers).size !== pageNumbers.length)
        context.addIssue({
            code: 'custom', path: ['pageInitials'], message: 'Each signed-policy page can be initialed once.',
        });
});
export const documentsFinalizeKnowledgeSignedPolicyArtifactSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.literal(1),
    artifact: platformStorageAttachedObjectReferenceSchema,
    reason: reasonSchema,
}).superRefine((value, context) => {
    if (value.artifact.actualContentType !== 'application/pdf')
        context.addIssue({
            code: 'custom', path: ['artifact', 'actualContentType'],
            message: 'A finalized signed-policy artifact must be a verified PDF.',
        });
});
export const documentsKnowledgeSignedPolicySourceSnapshotSchema = documentsKnowledgeSignedPolicyFactsResponseSchema;
export const documentsKnowledgeSignedPolicySignatureSchema = z.strictObject({
    signerInitials: z.string().trim().min(1).max(20),
    pageInitials: z.array(documentsKnowledgeSignedPolicyPageInitialSchema).max(250),
    typedName: z.string().trim().min(1).max(240),
    signatureSha256: sha256Schema,
    signatureArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable(),
    consentText: z.string().trim().min(1).max(4_000),
    consentAcceptedAt: z.string().datetime({ offset: true }),
});
export const documentsKnowledgeSignedPolicyRenderSchema = z.strictObject({
    bodyHtml: z.string().trim().min(1).max(1_000_000),
    bodyHtmlSha256: sha256Schema,
    printableHtml: z.string().trim().min(1).max(1_500_000),
    printableHtmlSha256: sha256Schema,
    pageFormat: z.literal('letter'),
    contentType: z.literal('text/html; charset=utf-8'),
});
const summarySchema = z.strictObject({
    contractVersion: z.literal(documentsKnowledgeSignedPolicyArtifactsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    publicId: z.string().uuid(),
    commandId: z.string().uuid(),
    status: z.enum(['signed', 'finalized']),
    revision: z.union([z.literal(1), z.literal(2)]),
    documentId: z.string().uuid(),
    versionId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    title: z.string().trim().min(1).max(200),
    membershipId: z.string().uuid(),
    acknowledgementId: z.string().uuid(),
    acknowledgedAt: z.string().datetime({ offset: true }),
    sourceContentHash: sha256Schema,
    signedProofHash: sha256Schema,
    printableHtmlSha256: sha256Schema,
    finalArtifactReference: platformStorageAttachedObjectReferenceSchema.nullable(),
    finalArtifactSha256: sha256Schema.nullable(),
    signedAt: z.string().datetime({ offset: true }),
    finalizedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    requestHash: sha256Schema,
});
export const documentsKnowledgeSignedPolicyArtifactSummarySchema = summarySchema.superRefine((value, context) => {
    const finalized = value.finalArtifactReference !== null && value.finalArtifactSha256 !== null &&
        value.finalizedAt !== null;
    if ((value.status === 'finalized') !== finalized || (value.revision === 2) !== finalized) {
        context.addIssue({ code: 'custom', message: 'Signed-policy artifact lifecycle must reconcile.' });
    }
});
export const documentsKnowledgeSignedPolicyArtifactSchema = summarySchema.extend({
    sourceSnapshot: documentsKnowledgeSignedPolicySourceSnapshotSchema,
    signature: documentsKnowledgeSignedPolicySignatureSchema,
    render: documentsKnowledgeSignedPolicyRenderSchema,
    acknowledgementText: z.string().trim().min(1).max(500),
    reason: reasonSchema,
}).superRefine((value, context) => {
    if (value.sourceSnapshot.tenantId !== value.tenantId ||
        value.sourceSnapshot.membershipId !== value.membershipId ||
        value.sourceSnapshot.documentId !== value.documentId ||
        value.sourceSnapshot.versionId !== value.versionId ||
        value.sourceSnapshot.versionNumber !== value.versionNumber ||
        value.sourceSnapshot.title !== value.title ||
        value.sourceSnapshot.sourceContentHash !== value.sourceContentHash ||
        value.render.printableHtmlSha256 !== value.printableHtmlSha256)
        context.addIssue({
            code: 'custom', path: ['sourceSnapshot'],
            message: 'The retained Knowledge fact and signed artifact must reconcile exactly.',
        });
    const finalized = value.finalArtifactReference !== null && value.finalArtifactSha256 !== null &&
        value.finalizedAt !== null;
    if ((value.status === 'finalized') !== finalized || (value.revision === 2) !== finalized) {
        context.addIssue({ code: 'custom', message: 'Signed-policy artifact lifecycle must reconcile.' });
    }
});
export const documentsKnowledgeSignedPolicyListQuerySchema = z.strictObject({
    documentId: z.string().uuid().nullable().default(null),
    membershipId: z.string().uuid().nullable().default(null),
    status: z.enum(['signed', 'finalized']).nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const documentsKnowledgeSignedPolicyArtifactResponseSchema = z.strictObject({
    data: documentsKnowledgeSignedPolicyArtifactSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const documentsKnowledgeSignedPolicyArtifactsResponseSchema = z.strictObject({
    data: z.array(documentsKnowledgeSignedPolicyArtifactSummarySchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=documents-knowledge-signed-policy.contract.js.map